import React from 'react';
import styled from 'styled-components';

const ServicesContainer = styled.section`
  id: services;
  padding: ${({ theme }) => `${theme.spacing.content.section} ${theme.spacing.content.base}`};
  background: ${({ theme }) => theme.colors.background.secondary};
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing['3xl']} ${theme.spacing.content.narrow}`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => `${theme.spacing['2xl']} ${theme.spacing.content.narrow}`};
  }
`;

const ServicesContent = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.wide};
  margin: 0 auto;
  text-align: center;
`;

const ServicesTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  }
`;

const ServicesSubtitle = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.light};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing['3xl']} 0;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const TierGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing['3xl']};
  align-items: stretch;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const ServiceTier = styled.div`
  background: ${({ theme, tier }) => {
    if (tier === 'premium') {
      return `
        linear-gradient(135deg, ${({ theme }) => theme.colors.accent.primary}99 0%, ${({ theme }) => theme.colors.accent.secondary}99 100%),
        url('/images/services/strategic-consultation-meeting.webp')`;
    } else if (tier === 'enterprise') {
      return `
        linear-gradient(135deg, ${({ theme }) => theme.colors.overlay.dark}99 0%, ${({ theme }) => theme.colors.overlay.medium}99 100%),
        url('/images/services/manufacturing-technology-background.webp')`;
    }
    return theme.colors.background.primary;
  }};
  background-size: cover;
  background-position: center;
  color: ${({ theme, tier }) => 
    (tier === 'premium' || tier === 'enterprise') ? theme.colors.text.inverse : theme.colors.text.primary};
  padding: ${({ theme }) => theme.spacing['2xl']};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme, tier }) => 
    tier === 'premium' ? theme.shadows['2xl'] : theme.shadows.card};
  transition: all ${({ theme }) => theme.transitions.base};
  text-align: left;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  
  ${({ tier }) => tier === 'premium' && `
    transform: scale(1.05);
    border: none;
  `}

  &:hover {
    transform: ${({ tier }) => 
      tier === 'premium' ? 'scale(1.08) translateY(-4px)' : 'translateY(-4px)'};
    box-shadow: ${({ theme }) => theme.shadows['2xl']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.xl};
    
    ${({ tier }) => tier === 'premium' && `
      transform: none;
      
      &:hover {
        transform: translateY(-4px);
      }
    `}
  }
`;

const TierBadge = styled.div`
  background: ${({ theme, tier }) => 
    tier === 'free' ? theme.colors.status.success :
    tier === 'premium' ? theme.colors.accent :
    tier === 'enterprise' ? theme.colors.secondary :
    theme.colors.primary};
  color: ${({ theme, tier }) => 
    tier === 'premium' ? theme.colors.primary : theme.colors.text.inverse};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.wide};
  display: inline-block;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const TierTitle = styled.h4`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }
`;

const TierPrice = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.black};
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;
  line-height: 1;

  .currency {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    vertical-align: super;
  }

  .period {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
    color: ${({ theme, tier }) => 
      tier === 'premium' ? theme.colors.text.inverse : theme.colors.text.muted};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
    
    .currency {
      font-size: ${({ theme }) => theme.typography.fontSize.base};
    }
  }
`;

const TierDescription = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  color: ${({ theme, tier }) => 
    (tier === 'premium' || tier === 'enterprise') ? theme.colors.text.inverse : theme.colors.text.secondary};
  opacity: ${({ tier }) => (tier === 'premium' || tier === 'enterprise') ? 0.9 : 1};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const TierFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
  flex-grow: 1;
`;

const Feature = styled.li`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  padding-left: ${({ theme }) => theme.spacing.lg};
  position: relative;
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
  color: ${({ theme, tier }) => 
    (tier === 'premium' || tier === 'enterprise') ? theme.colors.text.inverse : theme.colors.text.secondary};
  opacity: ${({ tier }) => (tier === 'premium' || tier === 'enterprise') ? 0.95 : 1};

  &:before {
    content: "✓";
    position: absolute;
    left: 0;
    color: ${({ theme, tier }) => 
      tier === 'premium' ? theme.colors.accent : theme.colors.status.success};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const TierCTA = styled.button`
  width: 100%;
  background: ${({ theme, tier }) => 
    tier === 'premium' ? theme.colors.white : theme.colors.primary.main};
  color: ${({ theme, tier }) => 
    tier === 'premium' ? theme.colors.primary : theme.colors.white};
  border: ${({ theme, tier }) => 
    tier === 'premium' ? `2px solid ${theme.colors.primary}` : 'none'};
  padding: ${({ theme }) => `${theme.spacing.base} ${theme.spacing.lg}`};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.wide};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.base};

  &:hover {
    background: ${({ theme, tier }) => 
      tier === 'premium' ? '#f3f4f6' : theme.colors.primary.dark};
    color: ${({ theme, tier }) => 
      tier === 'premium' ? theme.colors.primary : theme.colors.white};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.base}`};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const OutcomeSection = styled.div`
  background: ${({ theme, tier }) => 
    (tier === 'premium' || tier === 'enterprise') 
      ? 'rgba(255, 255, 255, 0.1)' 
      : theme.colors.background.primary};
  padding: ${({ theme }) => theme.spacing.base};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  margin-bottom: ${({ theme }) => theme.spacing.base};
  border-left: 4px solid ${({ theme, tier }) => 
    tier === 'free' ? theme.colors.status.success :
    tier === 'assessment' ? theme.colors.secondary :
    tier === 'premium' ? theme.colors.white :
    tier === 'enterprise' ? theme.colors.accent.primary :
    theme.colors.primary};
`;

const OutcomeTitle = styled.h5`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme, tier }) => 
    tier === 'premium' ? theme.colors.text.inverse : theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const OutcomeDescription = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme, tier }) => 
    tier === 'premium' ? theme.colors.text.inverse : theme.colors.text.muted};
  margin: 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
  font-style: italic;
`;

const ValueProposition = styled.div`
  background: 
    linear-gradient(135deg, ${({ theme }) => theme.colors.accent.primary}80 0%, ${({ theme }) => theme.colors.accent.secondary}80 100%),
    url('/images/services/manufacturing-transformation-infographic.jpg');
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing['3xl']};
  border-radius: ${({ theme }) => theme.borderRadius['2xl']};
  text-align: center;
  box-shadow: ${({ theme }) => theme.shadows.xl};
  position: relative;
  overflow: hidden;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing['2xl']};
    min-height: 150px;
  }
`;

const ValueText = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  margin: 0;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  color: ${({ theme }) => theme.colors.white};
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const ServicesSection = () => {
  console.log('ServicesSection updated - contrast and colors fixed');
  const handleScrollToContact = (e) => {
    e.preventDefault();
    const target = document.getElementById('contact');
    if (target) {
      const headerHeight = 80;
      const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <ServicesContainer id="services">
      <ServicesContent>
        <ServicesTitle>Progressive Engagement Model for Long-Term Success</ServicesTitle>
        <ServicesSubtitle>
          Build trust through value demonstration while progressing toward strategic partnership. 
          Each tier delivers measurable results and deepens our collaborative understanding.
        </ServicesSubtitle>

        <TierGrid>
          <ServiceTier $tier="free">
            <TierBadge $tier="free">Start Here</TierBadge>
            <TierTitle>Strategic Zoom Consultation</TierTitle>
            <TierPrice $tier="free">
              <span className="currency">$</span>0
              <span className="period">/consultation</span>
            </TierPrice>
            <TierDescription>
              30-minute strategic Zoom discussion to explore mutual fit and assess your current 
              IT/OT integration challenges without any commitment.
            </TierDescription>
            <TierFeatures>
              <Feature $tier="free">Mutual evaluation of strategic alignment</Feature>
              <Feature $tier="free">Current IT/OT challenge assessment</Feature>
              <Feature $tier="free">Transformation pathway overview</Feature>
              <Feature $tier="free">Timeline and investment expectations</Feature>
              <Feature $tier="free">No pressure, no commitment approach</Feature>
            </TierFeatures>
            <OutcomeSection $tier="free">
              <OutcomeTitle $tier="free">Outcome:</OutcomeTitle>
              <OutcomeDescription $tier="free">
                Clear understanding of engagement potential and strategic priorities
              </OutcomeDescription>
            </OutcomeSection>
            <TierCTA $tier="free" onClick={handleScrollToContact}>
              Schedule Free Zoom Consultation
            </TierCTA>
          </ServiceTier>

          <ServiceTier $tier="assessment">
            <TierBadge $tier="assessment">Deep Dive</TierBadge>
            <TierTitle>Stakeholder Evaluation</TierTitle>
            <TierPrice $tier="assessment">
              <span className="currency">$</span>2,500
              <span className="period">/engagement</span>
            </TierPrice>
            <TierDescription>
              3-4 hour intensive analysis across technical and strategic dimensions with 
              comprehensive stakeholder assessment and gap analysis.
            </TierDescription>
            <TierFeatures>
              <Feature $tier="assessment">Comprehensive stakeholder interviews</Feature>
              <Feature $tier="assessment">Organizational capability assessment</Feature>
              <Feature $tier="assessment">Technology infrastructure evaluation</Feature>
              <Feature $tier="assessment">Strategic roadmap development</Feature>
              <Feature $tier="assessment">Competitive positioning analysis</Feature>
            </TierFeatures>
            <OutcomeSection $tier="assessment">
              <OutcomeTitle $tier="assessment">Outcome:</OutcomeTitle>
              <OutcomeDescription $tier="assessment">
                Detailed transformation strategy with clear implementation priorities
              </OutcomeDescription>
            </OutcomeSection>
            <TierCTA $tier="assessment" onClick={handleScrollToContact}>
              Request Deep Assessment
            </TierCTA>
          </ServiceTier>

          <ServiceTier $tier="premium">
            <TierBadge $tier="premium">Most Popular</TierBadge>
            <TierTitle>Discovery Project</TierTitle>
            <TierPrice $tier="premium">
              <span className="currency">$</span>50,000
              <span className="period">/project</span>
            </TierPrice>
            <TierDescription>
              4-week intensive strategic planning engagement delivering complete implementation 
              roadmap and competitive advantage positioning.
            </TierDescription>
            <TierFeatures>
              <Feature $tier="premium">Complete technical architecture strategy</Feature>
              <Feature $tier="premium">Organizational change management planning</Feature>
              <Feature $tier="premium">Vendor evaluation and selection guidance</Feature>
              <Feature $tier="premium">ROI modeling and business case development</Feature>
              <Feature $tier="premium">Competitive advantage quantification</Feature>
              <Feature $tier="premium">Implementation timeline and resource planning</Feature>
            </TierFeatures>
            <OutcomeSection $tier="premium">
              <OutcomeTitle $tier="premium">Outcome:</OutcomeTitle>
              <OutcomeDescription $tier="premium">
                Complete blueprint for 5-10 year digital transformation journey
              </OutcomeDescription>
            </OutcomeSection>
            <TierCTA $tier="premium" onClick={handleScrollToContact}>
              Start Discovery Project
            </TierCTA>
          </ServiceTier>

          <ServiceTier $tier="enterprise">
            <TierBadge $tier="enterprise">Partnership</TierBadge>
            <TierTitle>Fractional CTO</TierTitle>
            <TierPrice $tier="enterprise">
              <span className="currency">$</span>5K-20K
              <span className="period">/month</span>
            </TierPrice>
            <TierDescription>
              Long-term strategic technology leadership through 5-10 year partnerships 
              delivering sustained competitive advantage.
            </TierDescription>
            <TierFeatures>
              <Feature $tier="enterprise">Ongoing strategic technology leadership</Feature>
              <Feature $tier="enterprise">Monthly implementation oversight</Feature>
              <Feature $tier="enterprise">Continuous competitive landscape monitoring</Feature>
              <Feature $tier="enterprise">Crisis response and decision support</Feature>
              <Feature $tier="enterprise">Quarterly business review and strategy refinement</Feature>
              <Feature $tier="enterprise">Direct executive access and consultation</Feature>
            </TierFeatures>
            <OutcomeSection $tier="enterprise">
              <OutcomeTitle $tier="enterprise">Outcome:</OutcomeTitle>
              <OutcomeDescription $tier="enterprise">
                Sustained competitive advantage through continuous strategic technology leadership
              </OutcomeDescription>
            </OutcomeSection>
            <TierCTA $tier="enterprise" onClick={handleScrollToContact}>
              Explore Partnership
            </TierCTA>
          </ServiceTier>
        </TierGrid>

        <ValueProposition>
          <ValueText>
            <strong>Lifetime Partnership Value: $2M-$10M+ per relationship.</strong> 
            Progressive engagement model builds trust through demonstrated results while establishing 
            the strategic foundation for long-term competitive advantage in manufacturing transformation.
          </ValueText>
        </ValueProposition>
      </ServicesContent>
    </ServicesContainer>
  );
};

export default ServicesSection;